import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaHeartbeat, FaNewspaper, FaAddressBook, FaUserFriends, FaBolt, FaGavel,
  FaArrowUp, FaArrowDown, FaArrowRight, FaRegSun,
} from 'react-icons/fa';
import { getDailyBrief } from '../../services/dailyBriefService';
import friendlyError from '../../utils/friendlyError';

const accent = 'var(--btn-primary)';
const borderSoft = 'rgba(255, 255, 255, 0.08)';
const panelBg = 'rgba(36, 52, 77, 0.72)';
const mono = "'JetBrains Mono', 'Fira Code', 'Consolas', monospace";

const Wrap = styled.section`
  position: relative;
  background:
    linear-gradient(180deg, rgba(0, 180, 216, 0.06), transparent 40%),
    ${panelBg};
  border: 1px solid ${borderSoft};
  border-radius: 12px;
  padding: 1.15rem 1.25rem 1.25rem;
  margin-bottom: 1.25rem;
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-bottom: 0.2rem;

  h2 {
    margin: 0;
    font-size: 1.15rem;
    color: var(--text-primary);
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg { color: ${accent}; font-size: 0.95rem; }
  }

  .date {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-left: auto;
  }
`;

const Lead = styled.p`
  margin: 0 0 1rem;
  font-size: 0.88rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 0.7rem;
`;

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.85rem 0.95rem;
  background: rgba(11, 19, 32, 0.5);
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${({ $tone }) =>
    $tone === 'bad' ? 'var(--alert-error)'
    : $tone === 'good' ? 'var(--alert-success)'
    : accent};
  border-radius: 8px;
  text-decoration: none;
  transition: border-color 0.15s ease, transform 0.15s ease, background 0.15s ease;

  &:hover {
    background: rgba(0, 180, 216, 0.05);
    transform: translateY(-1px);
    border-color: rgba(0, 180, 216, 0.35);
  }
  &:hover .go { opacity: 1; transform: translateX(0); }

  .top {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);

    svg { color: ${accent}; font-size: 0.72rem; }
    /* Siempre visible: es la señal de que la tarjeta lleva al detalle.
       Con opacity 0 hacía falta explicarlo por escrito en el subtítulo. */
    .go {
      margin-left: auto;
      opacity: 0.45;
      transition: opacity 0.15s ease, transform 0.15s ease;
      color: ${accent};
    }
  }

  .value {
    font-size: 1.55rem;
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1;
    display: flex;
    align-items: center;
    gap: 0.4rem;

    .delta {
      font-size: 0.78rem;
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      gap: 3px;
    }
    .delta.up { color: var(--alert-success); }
    .delta.down { color: var(--alert-error); }
  }

  .desc {
    font-size: 0.8rem;
    color: var(--text-secondary);
    line-height: 1.35;
  }
`;

const Skeleton = styled.div`
  height: 96px;
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(255,255,255,0.03), rgba(255,255,255,0.06), rgba(255,255,255,0.03));
  background-size: 200% 100%;
  animation: shimmer 1.3s infinite;
  @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
`;

const Note = styled.div`
  font-size: 0.82rem;
  color: var(--text-disabled);
  padding: 0.3rem 0;
`;

const fmt = (n) => Number(n || 0).toLocaleString('es-AR');

const DailyBrief = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    getDailyBrief()
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(friendlyError(e)); });
    return () => { alive = false; };
  }, []);

  const today = new Date().toLocaleDateString('es-AR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  const cards = [];
  if (data) {
    const { reputation: rep, press, casework: cw, competitors: comp, ai, hcd } = data;

    if (rep) {
      const delta = Math.round(rep.delta || 0);
      cards.push({
        tone: delta < 0 ? 'bad' : delta > 0 ? 'good' : 'neutral',
        icon: FaHeartbeat, label: 'Reputación', to: '/reputacion',
        value: rep.score,
        delta,
        desc: delta > 0 ? 'Mejoró respecto a ayer'
          : delta < 0 ? 'Bajó respecto a ayer'
          : 'Estable respecto a ayer',
      });
    }
    if (press) {
      cards.push({
        tone: press.new_24h > 0 ? 'neutral' : 'neutral',
        icon: FaNewspaper, label: 'Prensa (24h)', to: '/media',
        value: press.new_24h,
        desc: press.new_24h > 0
          ? `Notas nuevas en ${fmt(press.medios)} medios`
          : 'Sin notas nuevas en el día',
      });
    }
    if (cw) {
      cards.push({
        tone: cw.open > 0 ? 'bad' : 'good',
        // Va a /territorio, que es donde se listan los reclamos. /vecinos lista
        // personas y solo muestra reclamos anidados en el detalle de un vecino,
        // asi que los que tienen vecino_id NULL (todos los creados desde
        // Territorio o el cron) eran invisibles desde ahi.
        icon: FaAddressBook, label: 'Reclamos abiertos', to: '/territorio',
        value: cw.open,
        desc: cw.new_today > 0
          ? `${fmt(cw.new_today)} nuevo(s) hoy · ${fmt(cw.resolved_today)} resuelto(s)`
          : cw.open > 0 ? 'Esperando gestión' : 'Todo al día',
      });
    }
    if (comp) {
      cards.push({
        tone: 'neutral',
        icon: FaUserFriends, label: 'Competencia (24h)', to: '/competitors',
        value: comp.new_24h,
        desc: comp.new_24h > 0 ? 'Movimientos de rivales en prensa' : 'Sin movimientos nuevos',
      });
    }
    if (hcd && hcd.new_7d > 0) {
      cards.push({
        tone: 'neutral',
        icon: FaGavel, label: 'Concejo (7 días)', to: '/labor-hcd',
        value: hcd.new_7d,
        desc: 'Expedientes nuevos registrados',
      });
    }
    if (ai) {
      cards.push({
        tone: ai.failed_24h > 0 ? 'bad' : 'neutral',
        icon: FaBolt, label: 'IA (24h)', to: '/ai-live',
        value: ai.active > 0 ? ai.active : ai.done_24h,
        desc: ai.active > 0
          ? 'Investigaciones corriendo ahora'
          : `${fmt(ai.done_24h)} completadas${ai.failed_24h > 0 ? ` · ${fmt(ai.failed_24h)} con error` : ''}`,
      });
    }
  }

  return (
    <Wrap>
      <Head>
        <h2><FaRegSun /> Resumen del día</h2>
        <span className="date">{today}</span>
      </Head>
      <Lead>
        Lo que pasó en las últimas 24 horas en toda la operación, de un vistazo.
      </Lead>

      {error && <Note>No se pudo cargar el resumen · {error}</Note>}

      {!error && !data && (
        <Grid>
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} />)}
        </Grid>
      )}

      {!error && data && cards.length === 0 && (
        <Note>Todavía no hay actividad para resumir hoy.</Note>
      )}

      {!error && data && cards.length > 0 && (
        <Grid>
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <Card key={c.label} to={c.to} $tone={c.tone}>
                <span className="top">
                  <Icon /> {c.label}
                  <FaArrowRight className="go" />
                </span>
                <span className="value">
                  {fmt(c.value)}
                  {typeof c.delta === 'number' && c.delta !== 0 && (
                    <span className={`delta ${c.delta > 0 ? 'up' : 'down'}`}>
                      {c.delta > 0 ? <FaArrowUp /> : <FaArrowDown />}
                      {Math.abs(c.delta)}
                    </span>
                  )}
                </span>
                <span className="desc">{c.desc}</span>
              </Card>
            );
          })}
        </Grid>
      )}
    </Wrap>
  );
};

export default DailyBrief;
